import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AttachmentType, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import AttachmentTypesContext from "../../contexts/AttachmentTypesContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAttachmentTypes from "../../hooks/data/useAttachmentTypes";

function requiredAttachmentTypeId(row: AttachmentType): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Attachment type is missing its ID.");
    }

    return row.id;
}

const useAttachmentTypeColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<AttachmentType>[] = [
            {
                id: "name",
                label: "Name",
                scalarType: "text",
                accessor: (row) => row.name,
                flex: 1,
            },
            {
                id: "rank",
                label: "Rank",
                scalarType: "number",
                accessor: (row) => row.rank,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/attachment-types/edit/${requiredAttachmentTypeId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const AttachmentTypes: FC = () => {
    const columns = useAttachmentTypeColumns();

    const theme = useTheme();

    const context = useAttachmentTypes();

    const { data: attachmentTypes, loading: attachmentTypesLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Attachment Types`,
            exportTitle: `Attachment Types`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <AttachmentTypesContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/attachment-types/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Attachment Type
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Attachment Types"
                        loading={attachmentTypesLoading}
                        rows={attachmentTypes ?? []}
                        columns={columns}
                        getRowId={requiredAttachmentTypeId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </AttachmentTypesContext.Provider>
    );
};

export default AttachmentTypes;
